import type { ContextFeatureJsonValue } from '@/types/appRouter';
import { numericValueKinds, ruleRoleOptions, scorecardBarDurations, thresholdOperators, type ScorecardBarDuration } from './constants';
import type {
    ManualRuleSelectorKind,
    StrategyContext,
    ScorecardFeatureRow,
    ScorecardInitializationStatus,
    ScorecardPreviewRuleEvaluation,
    ScorecardRuleRole,
    ScorecardSelectorSchemaEntry
} from './types';

export const getNullableNumberSortValue = (value: number | string | null | undefined): number | undefined => {
    if (value == null) return undefined;

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : undefined;
};

export const parseStrategyContextFromQuery = (searchParams: URLSearchParams): StrategyContext | null => {
    const strategyId = Number(searchParams.get('strategyId'));
    return strategyId > 0 && Number.isFinite(strategyId) ? { strategyId } : null;
};

export const buildStrategyContextQueryString = (context: StrategyContext | null): string => {
    return context ? `strategyId=${context.strategyId}` : '';
};

export const getRuleRoleClassName = (ruleRole: ScorecardRuleRole): string => {
    if (ruleRole === 'veto') return 'border-red-800 bg-red-950/40 text-red-200';
    if (ruleRole === 'penalty') return 'border-amber-800 bg-amber-950/40 text-amber-200';
    return 'border-emerald-800 bg-emerald-950/40 text-emerald-200';
};

export const getRuleRoleLabel = (ruleRole: ScorecardRuleRole): string =>
    ruleRoleOptions.find((option) => option.value === ruleRole)?.label ?? ruleRole;

export const getPreviewRuleRoleCounts = (ruleEvaluations: readonly ScorecardPreviewRuleEvaluation[]) => ({
    boost: ruleEvaluations.filter((ruleEvaluation) => ruleEvaluation.matched && ruleEvaluation.ruleRole === 'boost').length,
    penalty: ruleEvaluations.filter((ruleEvaluation) => ruleEvaluation.matched && ruleEvaluation.ruleRole === 'penalty').length,
    veto: ruleEvaluations.filter((ruleEvaluation) => ruleEvaluation.matched && ruleEvaluation.ruleRole === 'veto').length
});

export const getScorecardInitializationLabel = (status: ScorecardInitializationStatus): string => {
    switch (status) {
        case 'pending':
            return 'Baselines not extracted yet';
        case 'extracting':
            return 'Extracting baselines';
        case 'ready':
            return 'Baselines ready';
        case 'failed':
            return 'Baseline extraction failed';
        default:
            return String(status);
    }
};

export const getScorecardInitializationHelp = (status: ScorecardInitializationStatus): string | null => {
    switch (status) {
        case 'pending':
            return 'finish editing, then extract baselines to make this scorecard ready';
        case 'extracting':
            return 'baseline extraction is running';
        case 'failed':
            return 'retry extraction before attaching';
        case 'ready':
            return null;
        default:
            return null;
    }
};

export const getScorecardInitializationClassName = (status: ScorecardInitializationStatus): string => {
    switch (status) {
        case 'ready':
            return 'text-green-400';
        case 'failed':
            return 'text-red-400';
        case 'extracting':
        case 'pending':
            return 'text-yellow-400';
        default:
            return 'text-zinc-400';
    }
};

export const getBaselineExtractionButtonLabel = (status: ScorecardInitializationStatus, isStartingExtraction: boolean): string => {
    if (isStartingExtraction || status === 'extracting') return 'Extracting…';

    switch (status) {
        case 'pending':
            return 'Extract Baselines';
        case 'failed':
            return 'Retry Baseline Extraction';
        case 'ready':
            return 'Baselines Ready';
        default:
            return 'Extract Baselines';
    }
};

export const isScorecardBarDuration = (value: string | null): value is ScorecardBarDuration => {
    return value !== null && scorecardBarDurations.includes(value as ScorecardBarDuration);
};

export const contextFeatureFormValueToString = (value: ContextFeatureJsonValue | undefined): string => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);

    return JSON.stringify(value);
};

export const parseContextFeatureFormValue = (rawValue: string, schemaEntry: ScorecardSelectorSchemaEntry): ContextFeatureJsonValue => {
    if (schemaEntry.allowedValues?.length) {
        const selectedIndex = schemaEntry.allowedValues.findIndex((allowedValue) => String(allowedValue) === rawValue);

        if (selectedIndex >= 0) {
            return schemaEntry.allowedValues[selectedIndex];
        }
    }

    switch (schemaEntry.type) {
        case 'boolean':
            return rawValue === 'true';

        case 'integer': {
            const parsed = Number.parseInt(rawValue, 10);

            if (!Number.isFinite(parsed)) throw new Error('Selector value must be an integer');

            return parsed;
        }

        case 'number': {
            const parsed = Number(rawValue);

            if (!Number.isFinite(parsed)) throw new Error('Selector value must be numeric');

            return parsed;
        }

        case 'timestamp': {
            const parsed = Number(rawValue);

            if (!Number.isFinite(parsed)) throw new Error('Selector value must be numeric');

            return parsed;
        }

        case 'json':
        case 'bindingKey':
        case 'duration':
        case 'enum':
        case 'levelKey':
        case 'string':
            return rawValue;
    }
};

export const parseFeatureValue = (rawValue: string, feature: ScorecardFeatureRow): ContextFeatureJsonValue => {
    if (feature.valueEnum?.length) {
        const selectedIndex = feature.valueEnum.findIndex((allowedValue) => String(allowedValue) === rawValue);

        if (selectedIndex >= 0) {
            return feature.valueEnum[selectedIndex];
        }
    }

    switch (feature.valueKind) {
        case 'boolean':
            return rawValue === 'true';

        case 'integer': {
            const parsed = Number(rawValue);

            if (!Number.isFinite(parsed)) throw new Error('Rule value must be numeric');

            return parsed;
        }

        case 'number': {
            const parsed = Number(rawValue);

            if (!Number.isFinite(parsed)) throw new Error('Rule value must be numeric');

            return parsed;
        }

        case 'enum':
        case 'multiEnum':
        case 'string':
        case 'unknown':
            return rawValue;
    }
};

export const selectorKindOptionsForFeature = (feature: ScorecardFeatureRow | null): { value: ManualRuleSelectorKind; label: string }[] => {
    if (!feature) return [{ value: 'value', label: 'Matches value' }];

    const options: { value: ManualRuleSelectorKind; label: string }[] = [{ value: 'value', label: 'Matches value' }];

    if (numericValueKinds.has(feature.valueKind)) {
        options.push({ value: 'range', label: 'Inside range' });

        if (feature.allowedOperators.some((operator) => thresholdOperators.includes(operator))) {
            options.push({ value: 'threshold', label: 'Numeric threshold' });
        }
    }

    return options;
};

export const selectorInputSupportsMultiValue = (feature: ScorecardFeatureRow): boolean => {
    return feature.cardinality === 'array' || feature.valueKind === 'multiEnum' || (feature.valueEnum?.length ?? 0) > 0;
};
